import type { ReactNode } from "react";
import classNames from "classnames";
import { useTemporarySideNavMode } from "@design-system/SideNav/SideNavProvider";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useInertRef } from "@hooks/useInertRef";
import { useTasksContext } from "./tasks.context";
import styles from "./TasksPageLayout.module.scss";

interface TasksPageLayoutProps {
    list: ReactNode;
    taskView: ReactNode;
}

/**
 * The Tasks page frame. While a task is open its view covers the page and the
 * side nav takes the task view's own remembered mode (mini until the user opens
 * it there); the list stays mounted underneath, hidden and inert, so its
 * scroll, pager order and live updates survive. Escape closes the task.
 */
export const TasksPageLayout = ({ list, taskView }: TasksPageLayoutProps) => {
    const { selectedTaskId, isOpenedByKeyboard, closeTask } = useTasksContext();
    const isTaskOpen = selectedTaskId !== null;
    const hasOpenModal = useHasOpenModal();
    const isEscapeActive = isTaskOpen && !hasOpenModal;
    const listRef = useInertRef(isTaskOpen);

    useTemporarySideNavMode({
        mode: isTaskOpen ? "mini" : null,
        rememberAs: "taskView",
    });
    useKeyboardCommand({
        keys: "Escape",
        callback: closeTask,
        options: {
            enabled: isEscapeActive,
            preventDefault: isEscapeActive,
            ignoreEventWhen: hasOpenMenu,
            description: "Close task",
        },
    });

    return (
        <div
            className={classNames(styles.layout, {
                [styles.isTaskOpen]: isTaskOpen,
            })}
        >
            <div ref={listRef} className={styles.list}>
                {list}
            </div>
            {isTaskOpen && (
                <div
                    className={classNames(styles.taskView, {
                        [styles.openedByKeyboard]: isOpenedByKeyboard,
                    })}
                >
                    {taskView}
                </div>
            )}
        </div>
    );
};
